/**
 * Base application error classes for E-Facture platform
 * All service errors should extend ApplicationError for consistent error handling
 *
 * DGI Compliance: Arrêté n°2025-0047/MEF/SG/DGI (05 février 2025)
 */
/**
 * Base application error class
 * All service errors should extend this class
 */
export class ApplicationError extends Error {
    code;
    statusCode;
    constructor(message, code, statusCode = 400) {
        super(message);
        this.code = code;
        this.statusCode = statusCode;
        this.name = this.constructor.name;
    }
}
export class InvoiceError extends ApplicationError {
    constructor(message, code, statusCode = 400) {
        super(message, code, statusCode);
    }
}
export class POSError extends ApplicationError {
    constructor(message, code, statusCode = 400) {
        super(message, code, statusCode);
    }
}
export class CashRegisterSessionError extends ApplicationError {
    constructor(message, code, statusCode = 400) {
        super(message, code, statusCode);
    }
}
export class ValidationError extends ApplicationError {
    constructor(message, code = "VALIDATION_ERROR") {
        super(message, code, 400);
    }
}
/**
 * Arbitrage d'une signature MCF d'issue inconnue.
 *
 * A class of its own rather than `InvoiceError`: the ambiguous row may
 * belong to the invoice path OR to the POS path (`posTransactionId`), so
 * neither `InvoiceError` nor `POSError` covers the surface without lying
 * about half of it.
 */
export class McfSignatureError extends ApplicationError {
    constructor(message, code, statusCode = 400) {
        super(message, code, statusCode);
    }
}
/**
 * An invoice the annex session (SECEF BF ST 1.0) cannot carry as it is.
 * Raised before C1h, so no MCF counter is touched. `line` is the 1-based
 * line number when the refusal concerns one line.
 */
export class SecefMappingError extends ApplicationError {
    code;
    line;
    constructor(message, code, line) {
        super(message, code, 422);
        this.code = code;
        this.line = line;
    }
}
/** A tenant's site MCF agents (SaaS): their enrolment and their WebSocket link. */
export class McfAgentError extends ApplicationError {
    constructor(message, code, statusCode) {
        super(message, code, statusCode);
    }
}
/** No agent socket is open for the tenant: nothing was sent. */
export class McfAgentNotConnectedError extends McfAgentError {
    constructor(message = "Agent MCF non connecté pour ce tenant", code = "MCF_AGENT_NOT_CONNECTED") {
        super(message, code, 503);
    }
}
/**
 * The agent's status (ACTIVE or revoked) could not be read, or is not read
 * yet: nothing was sent. It carries its own code and message; as a
 * subclass, every reader of `McfAgentNotConnectedError` still takes it for
 * « nothing sent ». A caller that must tell a failure of this server from
 * an absent agent tests this class first.
 */
export class McfAgentStatusUnreadableError extends McfAgentNotConnectedError {
    constructor() {
        super("État de l'agent MCF non vérifié : rien n'a été envoyé au MCF. Réessayez dans un instant.", "MCF_AGENT_ETAT_NON_VERIFIE");
    }
}
/** The agent socket closed while the request was in flight. */
export class McfAgentDisconnectedError extends McfAgentError {
    constructor() {
        super("Liaison avec l'agent MCF perdue avant sa réponse", "MCF_AGENT_DISCONNECTED", 502);
    }
}
/** The agent did not answer within the request timeout. */
export class McfAgentTimeoutError extends McfAgentError {
    constructor() {
        super("Délai d'attente dépassé pour la réponse de l'agent MCF", "MCF_AGENT_TIMEOUT", 504);
    }
}
/** The agent answered with an `error` message: its handler threw or it refused the message. */
export class McfAgentCommandError extends McfAgentError {
    constructor(message) {
        super(message, "MCF_AGENT_COMMAND_ERROR", 502);
    }
}
export class NLPError extends ApplicationError {
    constructor(message, code, statusCode = 400) {
        super(message, code, statusCode);
        this.name = "NLPError";
    }
}
//# sourceMappingURL=index.js.map